/**
 * Landing Video Reviews slider
 *
 * - The track is moved with a CSS transform (no native scrolling), so it behaves the same
 *   on iOS, Android and desktop, and stays correct when the landing page is scaled (zoom).
 * - Infinite loop: the cards are repeated before and after the real set; after a move
 *   lands on a repeat, the track jumps invisibly to the matching real card.
 * - Swipe / drag with mouse, touch or pen; arrows, dots and keyboard arrows also work.
 * - Tapping a card centres it and plays its video; every other video is paused.
 *   The thumbnail stays (with a spinner) until the video actually has frames to show.
 */
class LandingVideoSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.landing-video-reviews__track');
    this.prevBtn = this.querySelector('[data-action="prev"]');
    this.nextBtn = this.querySelector('[data-action="next"]');
    this.dots = Array.from(this.querySelectorAll('.landing-video-reviews__dot'));

    const originals = Array.from(this.querySelectorAll('.landing-video-reviews__card'));
    if (!this.track || !originals.length) return;

    this.count = originals.length;
    this.isLooping = this.count > 1;
    if (this.isLooping) this.buildClones(originals);

    // DOM order: [repeat set][real set][repeat set] when looping
    this.cards = Array.from(this.track.querySelectorAll('.landing-video-reviews__card'));
    this.offset = this.isLooping ? this.count : 0;
    this.index = this.offset + Math.floor(this.count / 2);

    this.bindControls();
    this.bindCards();
    this.bindSwipe();

    this.onResize = () => this.goTo(this.index, false);
    window.addEventListener('resize', this.onResize, { passive: true });

    // Lay out once styles/fonts have applied
    requestAnimationFrame(() => this.goTo(this.index, false));
  }

  disconnectedCallback() {
    this.pauseAll();
    window.removeEventListener('resize', this.onResize);
  }

  buildClones(originals) {
    const makeClone = (card) => {
      const clone = card.cloneNode(true);
      clone.classList.remove('is-playing', 'is-started', 'is-loading');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('button').forEach((btn) => btn.setAttribute('tabindex', '-1'));
      return clone;
    };

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    originals.forEach((card) => {
      before.appendChild(makeClone(card));
      after.appendChild(makeClone(card));
    });
    this.track.insertBefore(before, originals[0]);
    this.track.appendChild(after);
  }

  /* ---------------- Positioning ---------------- */
  metrics() {
    const card = this.cards[0];
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return { cardW: card.offsetWidth, step: card.offsetWidth + gap, viewW: this.track.clientWidth };
  }

  /** x offset (local CSS px) that centres card `i` in the track */
  offsetFor(i) {
    const { cardW, step, viewW } = this.metrics();
    return viewW / 2 - (i * step + cardW / 2);
  }

  setX(x, animate) {
    this.currentX = x;
    this.track.classList.toggle('is-animating', animate);
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  goTo(i, animate = true) {
    this.index = Math.max(0, Math.min(this.cards.length - 1, i));
    this.setX(this.offsetFor(this.index), animate);
    this.setActiveDot();

    if (!animate) return;
    clearTimeout(this.settleTimer);
    const done = () => {
      this.track.removeEventListener('transitionend', onEnd);
      clearTimeout(this.settleTimer);
      this.normalize();
    };
    const onEnd = (e) => {
      if (e.target === this.track && e.propertyName === 'transform') done();
    };
    this.track.addEventListener('transitionend', onEnd);
    this.settleTimer = setTimeout(done, 600); // fallback if transitionend doesn't fire
  }

  /** If we landed on a repeated card, jump invisibly to the same card in the real set. */
  normalize() {
    if (!this.isLooping || this.isAnyPlaying()) return;
    const real = this.offset + ((((this.index - this.offset) % this.count) + this.count) % this.count);
    if (real !== this.index) this.goTo(real, false);
  }

  step(direction) {
    this.pauseAll();
    this.normalize();
    let next = this.index + direction;
    if (!this.isLooping) next = (next + this.count) % this.count;
    this.goTo(next);
  }

  setActiveDot() {
    const logical = (((this.index - this.offset) % this.count) + this.count) % this.count;
    this.dots.forEach((dot, i) => {
      const active = i === logical;
      dot.classList.toggle('landing-video-reviews__dot--active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  /* ---------------- Controls ---------------- */
  bindControls() {
    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.pauseAll();
        this.normalize();
        this.goTo(this.offset + i);
      });
    });
    this.addEventListener('keydown', (e) => {
      if (e.target.closest('.landing-video-reviews__play-btn')) return;
      if (e.key === 'ArrowLeft') this.step(-1);
      else if (e.key === 'ArrowRight') this.step(1);
    });
  }

  /* ---------------- Swipe / drag ---------------- */
  bindSwipe() {
    let startX = 0;
    let startY = 0;
    let baseX = 0;
    let pointerId = null;
    let dragging = false;
    let scale = 1;

    const onDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      baseX = this.currentX || 0;
      dragging = false;
      // Screen px -> local px (the landing page may be scaled with CSS zoom)
      const rect = this.track.getBoundingClientRect();
      scale = this.track.offsetWidth ? rect.width / this.track.offsetWidth : 1;
    };

    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        dragging = true;
        this.track.setPointerCapture?.(pointerId);
        this.track.classList.add('is-dragging');
        this.pauseAll();
      }
      this.setX(baseX + dx / scale, false);
    };

    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');
      // The click that follows a drag (can arrive late on touch) shouldn't play a video
      this.suppressClickUntil = Date.now() + 400;

      const dx = (e.clientX - startX) / scale;
      const { step } = this.metrics();
      // Move by as many cards as were dragged past (at least one if > 20% of a card)
      let moved = Math.round(-dx / step);
      if (moved === 0 && Math.abs(dx) > step * 0.2) moved = dx < 0 ? 1 : -1;
      this.goTo(this.index + moved);
    };

    this.track.addEventListener('pointerdown', onDown);
    this.track.addEventListener('pointermove', onMove);
    this.track.addEventListener('pointerup', onUp);
    this.track.addEventListener('pointercancel', onUp);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  /* ---------------- Video playback ---------------- */
  bindCards() {
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => {
        if (Date.now() < (this.suppressClickUntil || 0)) return;
        this.onCardClick(card, i);
      });

      const video = card.querySelector('video');
      if (!video) return;
      video.loop = true;
      video.addEventListener('play', () => this.setPlaying(card, true));
      video.addEventListener('pause', () => {
        this.setPlaying(card, false);
        card.classList.remove('is-loading');
      });
      video.addEventListener('waiting', () => card.classList.add('is-loading'));
      // Only swap the thumbnail for the video once it has frames to show
      video.addEventListener('playing', () => {
        card.classList.remove('is-loading');
        card.classList.add('is-started');
      });
      // A failing <source> fires 'error' on the source element; capture it here
      video.addEventListener(
        'error',
        () => {
          card.classList.remove('is-loading', 'is-started');
          this.setPlaying(card, false);
        },
        true
      );
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        video.play().catch(() => {});
      });
    });
  }

  /** Index of the same card in the real (middle) set */
  realIndex(i) {
    if (!this.isLooping) return i;
    return this.offset + ((((i - this.offset) % this.count) + this.count) % this.count);
  }

  onCardClick(clickedCard, clickedIndex) {
    let i = clickedIndex;
    let card = clickedCard;

    if (i !== this.index) {
      this.pauseAll();
      // If a repeated card was tapped, first shift the whole track (invisibly: the
      // repeats look identical) so that card's real copy sits where it was tapped,
      // then centre and play the real one. This keeps the slider inside the real set
      // even when videos are played one after another, so it never runs off the end.
      const real = this.realIndex(i);
      if (real !== i) {
        this.goTo(this.index + (real - i), false);
        // Force the jump to apply before animating, so it doesn't animate itself
        void this.track.offsetWidth;
        i = real;
        card = this.cards[i];
      }
      this.goTo(i);
    }

    const video = card.querySelector('video');
    if (!video) return;

    if (video.paused) {
      this.pauseAll(video);
      if (video.readyState < 3) card.classList.add('is-loading');
      const p = video.play();
      if (p) {
        p.catch(() => {
          card.classList.remove('is-loading');
          this.setPlaying(card, false);
        });
      }
    } else {
      video.pause();
    }
  }

  pauseAll(except = null) {
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (video && video !== except && !video.paused) video.pause();
    });
  }

  isAnyPlaying() {
    return this.cards.some((card) => {
      const video = card.querySelector('video');
      return video && !video.paused;
    });
  }

  setPlaying(card, isPlaying) {
    card.classList.toggle('is-playing', isPlaying);
    const btn = card.querySelector('.landing-video-reviews__play-btn');
    if (btn) btn.setAttribute('aria-label', isPlaying ? btn.dataset.labelPause : btn.dataset.labelPlay);
  }
}

if (!customElements.get('landing-video-slider')) {
  customElements.define('landing-video-slider', LandingVideoSlider);
}
